import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import qualityCapacityReportService from '../services/qualityCapacityReportService';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Download,
  FileSpreadsheet,
  Loader2,
} from 'lucide-react';

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `${(num * 100).toFixed(2)}%`;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const QualityCapacityReportRunDetailPage = () => {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const loadRun = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await qualityCapacityReportService.getRun(runId);
      setRun(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || '加载报表详情失败');
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    loadRun();
  }, [loadRun]);

  const handleExport = async () => {
    try {
      setExporting(true);
      setError('');
      await qualityCapacityReportService.exportRun(runId);
    } catch (err) {
      setError(err.response?.data?.error || err.message || '导出失败');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Layout title="报表详情" activeTab="reports">
        <div className="max-w-[1600px] mx-auto py-12 px-4 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground mt-4">加载报表详情...</p>
        </div>
      </Layout>
    );
  }

  if (error || !run) {
    return (
      <Layout title="报表详情" activeTab="reports">
        <div className="max-w-[1600px] mx-auto py-6 px-4">
          <Button variant="outline" onClick={() => navigate('/reports/quality-capacity')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> 返回列表
          </Button>
          <div className="mt-6 p-4 rounded-md bg-destructive/10 text-destructive text-sm">
            {error || '报表不存在'}
          </div>
        </div>
      </Layout>
    );
  }

  const dailyTotals = run.daily_totals || [];
  const items = run.items || [];
  const summary = run.summary || {};
  const scrapSummary = run.scrap_reason_summary || [];

  return (
    <Layout title="质量与产能报表详情" activeTab="reports">
      <div className="max-w-[1600px] mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/reports/quality-capacity')}
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> 返回
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{run.report_name}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                运行编号：{run.run_id} · 生成时间：{formatDateTime(run.created_at)}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleExport} disabled={exporting || items.length === 0}>
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              <span className="ml-2">导出 Excel</span>
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 rounded-md bg-destructive/10 text-destructive text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">日期范围</p>
              <p className="text-lg font-semibold mt-1">
                {run.filters?.start_date} ~ {run.filters?.end_date}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">交检总数</p>
              <p className="text-lg font-semibold mt-1">{formatNumber(summary.total_inspected)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">良品数</p>
              <p className="text-lg font-semibold mt-1">{formatNumber(summary.total_good)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">报废数</p>
              <p className="text-lg font-semibold mt-1">{formatNumber(summary.total_scrapped)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">良率</p>
              <p className="text-lg font-semibold mt-1">{formatPercent(summary.yield_rate)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">目标良率</p>
              <p className="text-lg font-semibold mt-1">{formatPercent(summary.target_yield_rate)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">总产值</p>
              <p className="text-lg font-semibold mt-1">{formatNumber(summary.total_output_value)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">是否达标</p>
              <p className={`text-lg font-semibold mt-1 ${summary.is_target_met ? 'text-green-600' : 'text-red-600'}`}>
                {summary.is_target_met ? '是' : '否'}
              </p>
            </CardContent>
          </Card>
        </div>

        <Card className="mb-6 overflow-hidden">
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              工单/产品明细
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="whitespace-nowrap">任务单号</TableHead>
                  <TableHead className="whitespace-nowrap">产品编号</TableHead>
                  <TableHead className="whitespace-nowrap">产品名称</TableHead>
                  <TableHead className="whitespace-nowrap">规格</TableHead>
                  {dailyTotals.map((d) => (
                    <TableHead key={d.date} className="text-center whitespace-nowrap">
                      {new Date(d.date).getDate()}
                    </TableHead>
                  ))}
                  <TableHead className="text-center whitespace-nowrap">汇总</TableHead>
                  <TableHead className="text-right whitespace-nowrap">产值</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5 + dailyTotals.length + 2} className="text-center text-muted-foreground">
                      暂无数据
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="whitespace-nowrap">{item.production_order_number}</TableCell>
                      <TableCell className="whitespace-nowrap">{item.finished_product_code}</TableCell>
                      <TableCell className="whitespace-nowrap">{item.finished_product_name}</TableCell>
                      <TableCell className="whitespace-nowrap">{item.specification}</TableCell>
                      {dailyTotals.map((d) => {
                        const dayData = item.daily_quantities[d.date] || { completed: 0, scrapped: 0 };
                        const inspected = dayData.completed + dayData.scrapped;
                        return (
                          <TableCell key={d.date} className="text-center whitespace-nowrap">
                            {inspected > 0 ? inspected : '-'}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center font-medium whitespace-nowrap">
                        {item.totals.inspected}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {item.output_value ? formatNumber(item.output_value) : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="mb-6 overflow-hidden">
          <CardHeader>
            <CardTitle className="text-base font-medium">汇总</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="whitespace-nowrap">汇总项</TableHead>
                  {dailyTotals.map((d) => (
                    <TableHead key={d.date} className="text-center whitespace-nowrap">
                      {new Date(d.date).getDate()}
                    </TableHead>
                  ))}
                  <TableHead className="text-center whitespace-nowrap">合计</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium whitespace-nowrap">交检总数/支</TableCell>
                  {dailyTotals.map((d) => (
                    <TableCell key={d.date} className="text-center">{d.inspected || '-'}</TableCell>
                  ))}
                  <TableCell className="text-center font-medium">{summary.total_inspected}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium whitespace-nowrap">良品数/支</TableCell>
                  {dailyTotals.map((d) => (
                    <TableCell key={d.date} className="text-center">{d.good || '-'}</TableCell>
                  ))}
                  <TableCell className="text-center font-medium">{summary.total_good}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium whitespace-nowrap">良率</TableCell>
                  {dailyTotals.map((d) => (
                    <TableCell key={d.date} className="text-center">{formatPercent(d.yield_rate)}</TableCell>
                  ))}
                  <TableCell className="text-center font-medium">{formatPercent(summary.yield_rate)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium whitespace-nowrap">良率目标</TableCell>
                  {dailyTotals.map((d) => (
                    <TableCell key={d.date} className="text-center">{formatPercent(summary.target_yield_rate)}</TableCell>
                  ))}
                  <TableCell className="text-center font-medium">{formatPercent(summary.target_yield_rate)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base font-medium">报废原因明细</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>缺陷类型</TableHead>
                  <TableHead>不良项</TableHead>
                  <TableHead className="text-right">数量</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scrapSummary.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground">
                      暂无报废记录
                    </TableCell>
                  </TableRow>
                ) : (
                  scrapSummary.map((reason, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{reason.category}</TableCell>
                      <TableCell>{reason.reason_code}（{reason.item}）</TableCell>
                      <TableCell className="text-right">{reason.quantity}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default QualityCapacityReportRunDetailPage;
